import React from "react";
import { HeadFC, PageProps, graphql } from "gatsby";
import { LastScrollCTADynamicZoneModel } from "@models/strapi-components/last-scroll-cta";
import { WebinarPointsModel } from "@models/webinar";
import LastScrollCTA from "@components/StrapiComponents/LastScrollCTA";
import WebinarPoints from "@components/webinar/WebinarPoints";
import Layout from "@components/common/Layout";
import { HeroBannerPropsModel } from "@models/hero-banner";
import { SeoDataModel } from "@models/seo";
import { PageContextModel } from "@models/page";
import SEO from "@components/common/Layout/SEO";
import { InfoBlockModel } from "@models/strapi-components/success-story";
import { StrapiGatsbyImageType } from "@models/strapi-components/common";
import ImageRenderer from "@components/common/Image";
import Quote from "@components/common/Quote";
import DemoCustomerTiles from "@components/common/DemoCustomerTiles";
import VizWrapper from "@components/common/Base/VizWrapper";
import WebinarHero from "@components/webinar/Hero";
import Title from "@components/common/Title";
import NewLink from "@components/common/NewLink";
import { HubSpotFormDataModel } from "@models/strapi-components/hubspot";
import FormBanner from "@components/common/HeroBanner/FormBanner";

export interface CustomerSuccessDataModel {
  strapiSuccessStory: {
    id: string;
    title: string;
    lastScrollCTA: LastScrollCTADynamicZoneModel;
    infoBlock: InfoBlockModel;
    successPoints: WebinarPointsModel;
    topBanner: HeroBannerPropsModel;
    seo: SeoDataModel;
    quote: string;
    quoteAuthor: string;
    quoteAuthorSubTitle: string;
    quoteBgColor: string;
    logo: StrapiGatsbyImageType;
    formName: string | null;
    summary: {
      internal: {
        content: string;
      };
      Founded: string;
      Headquarters: string;
      Industry: string;
      Product_Range: string;
    };
  };
  hubspotForm: HubSpotFormDataModel | null;
}

export interface SummaryPropsModel {
  logo: StrapiGatsbyImageType | undefined;
  summaryObj: {
    Founded: string;
    Headquarters: string;
    Industry: string;
    Product_Range: string;
  };
  downloadLink: string;
  isHubspotFormOnPage: boolean;
}

const Summary = (props: SummaryPropsModel) => {
  const { logo, summaryObj, downloadLink, isHubspotFormOnPage } = props;

  const renderSummaryObject = () => {
    return Object.keys(summaryObj).map((key) => {
      const value = summaryObj?.[key as keyof typeof summaryObj];
      if (!value) return <></>;
      return (
        <div className="text-left mb-3 xl:mb-4 last:mb-0" key={key}>
          <Title
            variant="paragraph"
            className="text-zinc-600 font-light"
            titleText={key}
          />
          <Title
            variant="paragraph"
            className="font-semibold"
            titleText={value}
          />
        </div>
      );
    });
  };

  return (
    <div className="w-full bg-white rounded-2xl shadow-lg sticky top-0 right-0 p-8 flex flex-col items-start">
      <div className="flex justify-center">
        {logo && <ImageRenderer image={logo} />}
      </div>
      <div className="mt-8 w-full grid grid-cols-2 sm:block">
        {" "}
        {renderSummaryObject()}
      </div>
      <div className="">
        <NewLink
          className="mt-8 md:h-auto"
          style={{
            height: "43px",
            fontSize: "14px",
            // fontWeight: "normal",
          }}
          href={downloadLink}
          target={isHubspotFormOnPage ? "_self" : "_blank"}
          referrerPolicy="no-referrer"
        >
          DOWNLOAD
        </NewLink>
      </div>
    </div>
  );
};

const OneSuccessStory = (props: PageProps<CustomerSuccessDataModel>) => {
  const { data, location } = props ?? {};
  const { href: pageUrl } = location ?? {};
  const { strapiSuccessStory, hubspotForm } = data ?? {};
  const {
    lastScrollCTA,
    topBanner,
    logo,
    successPoints,
    quote,
    quoteAuthor,
    quoteAuthorSubTitle,
    quoteBgColor,
    infoBlock,
    summary,
    formName,
  } = strapiSuccessStory ?? {};
  const storyDownloadLink = topBanner?.ctaBtnLink ?? "#";
  const isHubspotFormOnPage = !!(hubspotForm && formName);

  const renderHeroBanner = () => {
    if (formName && hubspotForm) {
      return (
        <FormBanner
          subTitle=""
          {...topBanner}
          hubspotForm={hubspotForm}
          pageUrl={pageUrl}
        />
      );
    }
    return (
      <WebinarHero
        title={topBanner?.title}
        bigHeader={topBanner?.bigHeader}
        ctaLink={lastScrollCTA?.ctaLink}
        ctaText="READ SUCCESS STORY"
      />
    );
  };

  return (
    <Layout
      style={{
        height: "100dvh",
        overflowY: "auto",
      }}
    >
      {renderHeroBanner()}
      <DemoCustomerTiles
        tiles={infoBlock?.tiles}
        divColor={infoBlock.divColor}
      />
      <VizWrapper className="px-8 xl:px-40 pb-0 md:pb-0 xl:pb-0 relative h-max">
        <div className="flex gap-8 sm:pb-12 md:pb-[60px] xl:pb-20">
          <div className="grow">
            <div className="block sm:hidden mb-8">
              <Summary
                logo={logo}
                summaryObj={{ ...summary }}
                downloadLink={storyDownloadLink}
                isHubspotFormOnPage={isHubspotFormOnPage}
              />
            </div>
            <Quote
              quote={quote}
              quoteAuthor={quoteAuthor}
              quoteAuthorSubTitle={quoteAuthorSubTitle}
              quoteBgColor={quoteBgColor}
            />
            <WebinarPoints webinarPointsData={successPoints} />
          </div>
          <div className="hidden sm:flex w-[250px] md:w-[300px] lg:w-[350px] justify-end sticky top-[77px] h-min shrink-0">
            <Summary
              logo={logo}
              summaryObj={{ ...summary }}
              downloadLink={storyDownloadLink}
              isHubspotFormOnPage={isHubspotFormOnPage}
            />
          </div>
        </div>
      </VizWrapper>
      <LastScrollCTA componentData={lastScrollCTA} />
    </Layout>
  );
};

export default OneSuccessStory;

export const query = graphql`
  query CustomerSuccess($slug: String, $formName: String) {
    strapiSuccessStory(slug: { eq: $slug }) {
      id
      title
      slug
      quote
      quoteAuthor
      quoteAuthorSubTitle
      quoteBgColor
      #      formName
      lastScrollCTA {
        id
        innerHtml
        ctaText
        ctaLink
        variant
      }
      seo {
        ...seoFragment
      }
      topBanner {
        ...topBannerFragment
      }
      lastScrollCTA {
        ctaLink
        ctaText
        innerHtml
        variant
      }
      summary {
        Founded(locale: "en")
        Headquarters
        Industry
        Product_Range
      }
      successPoints {
        title
        id
        strapi_id
        points {
          title
          subTitle
          id
          icon
        }
      }
      infoBlock {
        divColor
        title
        variant
        id
        tiles {
          id
          icon
          color
          title
          subTitle
        }
      }
      logo {
        localFile {
          childImageSharp {
            gatsbyImageData(
              height: 50
              quality: 100
              placeholder: NONE
              formats: NO_CHANGE
            )
          }
        }
      }
    }
    hubspotForm(name: { eq: $formName }) {
      id
      guid
      portalId
      submitText
      formFieldGroups {
        default
        fields {
          name
          label
          type
          fieldType
          description
          groupName
          displayOrder
          required
          enabled
          hidden
          defaultValue
          isSmartField
          unselectedLabel
          placeholder
          labelHidden
          propertyObjectType
          objectTypeId
          options {
            label
            value
          }
        }
      }
    }
  }
`;

export const Head: HeadFC<CustomerSuccessDataModel, PageContextModel> = ({
  data,
  location,
}) => {
  const { title, seo } = data?.strapiSuccessStory ?? {};

  const pathname = location?.pathname;
  return <SEO title={title} pathname={pathname} {...(seo ?? {})} />;
};
